import React, {
  ForwardRefRenderFunction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Button,
  Checkbox,
  Divider,
  Drawer,
  Modal,
  notification,
  Select,
  Spin,
} from 'antd';
import Form, { FormSchema } from '@sezenta/antd-schema-form';
import { useBusinessContext } from '../../context/BusinessContext';
import { Account } from '../../dto';
import { Item, ItemType } from '../../dto/Item';
import { useEvents } from '../../common/EventContext';
import { useConnection } from '../../connection/connection';
import { setFormValidationErrors } from '../../common/Utils';

export type ItemEditModalInstance = {
  create: () => void;
  edit: (itemId: number) => void;
};

type Props = {};

const ItemEditModal: ForwardRefRenderFunction<ItemEditModalInstance, Props> = (
  props,
  ref,
) => {
  const [title, setTitle] = useState<string>('Create Item');
  const [visible, setVisible] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [form] = Form.useForm<Item>();
  const events = useEvents();
  const connection = useConnection();
  const [salesEnabled, setSalesEnabled] = useState<boolean>(true);
  const [purchasesEnabled, setPurchasesEnabled] = useState<boolean>(true);
  const [inventoryEnabled, setInventoryEnabled] = useState<boolean>(true);
  const [openingStockAvailable, setOpeningStockAvailable] =
    useState<boolean>(false);
  const [item, setItem] = useState<Item>();
  const business = useBusinessContext();
  useImperativeHandle(
    ref,
    () => ({
      create: () => {
        setVisible(true);
        setSaving(false);
        form.resetFields();
        setItem(undefined);
        form.setFieldsValue({ itemType: ItemType.Good, returnable: true });
      },
      edit: (itemId: number) => {
        setLoading(true);
        setVisible(true);
        setSaving(false);
        form.resetFields();
        connection
          .get<Item>(`${business.businessId}/items/${itemId}`)
          .then((res) => {
            form.setFieldsValue(res.data);
            setPurchasesEnabled(res.data.purchasable);
            setSalesEnabled(res.data.sellable);
            setInventoryEnabled(res.data.trackInventory);
            setItem(res.data);
            setLoading(false);
          })
          .catch((err) => {
            console.log(err);
            Modal.error({ title: 'Error', content: 'Something went wrong!' });
            setVisible(false);
          });
      },
    }),
    [business.businessId, connection, form],
  );

  const basicSchema = useMemo(() => {
    const formSchema: FormSchema = [
      {
        id: 'itemType',
        type: 'radio',
        name: 'Type',
        props: { options: ['Good', 'Service'] },
        options: {
          rules: [{ required: true, message: 'Item type is required' }],
        },
      },
      {
        id: 'name',
        type: 'string',
        name: 'Name',
        layout: { xs: 24, md: 12 },
        options: {
          rules: [{ required: true, message: 'Name is required' }],
        },
      },
      {
        id: 'sku',
        type: 'string',
        name: 'SKU',
        layout: { xs: 24, md: 12 },
      },
      {
        id: 'unit',
        type: 'select',
        name: 'Unit',
        layout: { xs: 24, md: 12 },
        props: {
          showSearch: true,
          options: [
            'box',
            'cm',
            'dz',
            'ft',
            'g',
            'in',
            'kg',
            'km',
            'hrs',
            'lb',
            'ml',
            'mg',
            'm',
            'pcs',
          ],
        },
        options: {
          rules: [{ required: true, message: 'Unit is required' }],
        },
      },
      {
        id: 'returnable',
        type: 'radio',
        name: 'Type',
        layout: { xs: 24, md: 12 },
        props: {
          options: [
            { label: 'Returnable', value: true },
            { label: 'Non Returnable', value: false },
          ],
        },
      },
    ];
    return formSchema;
  }, []);

  const otherInfoSchema = useMemo(() => {
    const formSchema: FormSchema = [
      {
        id: 'dimensions',
        name: 'Dimensions',
        layout: { xs: 24, md: 12 },
        type: {
          object: [
            { id: 'width', name: 'Width', type: 'number', layout: { xs: 6 } },
            { id: 'length', name: 'Length', type: 'number', layout: { xs: 6 } },
            { id: 'height', name: 'Height', type: 'number', layout: { xs: 6 } },
            {
              id: 'unit',
              name: 'Unit',
              type: 'select',
              props: { options: ['cm', 'in', 'm', 'ft'] },
              layout: { xs: 6 },
            },
          ],
        },
      },
      {
        id: 'weight',
        name: 'Weight',
        layout: { xs: 24, md: 12 },
        type: {
          object: [
            {
              id: 'amount',
              name: 'Weight',
              type: 'number',
              layout: { xs: 18 },
            },
            {
              id: 'unit',
              name: 'Unit',
              type: 'select',
              props: { options: ['kg', 'lb'] },
              layout: { xs: 6 },
            },
          ],
        },
      },
      {
        id: 'manufacturer',
        type: 'string',
        name: 'Manufacturer',
        layout: { xs: 24, md: 12 },
      },
      {
        id: 'brand',
        type: 'string',
        name: 'Brand',
        layout: { xs: 24, md: 12 },
      },
    ];
    return formSchema;
  }, []);

  const salesInfoSchema = useMemo(() => {
    const formSchema: FormSchema = [
      {
        id: 'sellingPrice',
        name: 'Selling Price',
        type: 'price',
        props: { disabled: !salesEnabled },
        options: {
          rules: [{ required: salesEnabled, message: 'Price is required' }],
        },
      },
      {
        id: 'sellingAccount',
        type: 'select2',
        name: 'Account',
        props: {
          disabled: !salesEnabled,
          showSearch: true,
          optionFilterProp: 'label',
          options: business.getAccountsForSelect(
            (account) => account.accountCategory === 'Income',
          ),
        },
        options: {
          rules: [{ required: salesEnabled, message: 'Account is required' }],
        },
      },
      {
        id: 'sellingDescription',
        name: 'Description',
        type: 'text',
        props: { disabled: !salesEnabled },
      },
    ];
    return formSchema;
  }, [business, salesEnabled]);

  const purchaseInfoSchema = useMemo(() => {
    const formSchema: FormSchema = [
      {
        id: 'purchasingPrice',
        name: 'Purchasing Price',
        type: 'price',
        props: { disabled: !purchasesEnabled },
        options: {
          rules: [{ required: purchasesEnabled, message: 'Price is required' }],
        },
      },
      {
        id: 'purchasingAccount',
        type: 'select2',
        name: 'Account',
        props: {
          disabled: !purchasesEnabled,
          showSearch: true,
          optionFilterProp: 'label',
          options: business.getAccountsForSelect(
            (account) => account.accountCategory === 'Expenses',
          ),
        },
        options: {
          rules: [
            { required: purchasesEnabled, message: 'Account is required' },
          ],
        },
      },
      {
        id: 'purchasingDescription',
        name: 'Description',
        type: 'text',
        props: { disabled: !purchasesEnabled },
      },
    ];
    return formSchema;
  }, [business, purchasesEnabled]);

  const inventoryInfoSchema = useMemo(() => {
    const formSchema: FormSchema = [
      {
        id: 'inventoryAccount',
        type: 'select2',
        name: 'Inventory Account',
        props: {
          disabled: !inventoryEnabled,
          showSearch: true,
          optionFilterProp: 'label',
          options: business.getAccountsForSelect(
            (account) => account.accountTypeName === 'Inventory',
          ),
        },
        options: {
          rules: [
            { required: inventoryEnabled, message: 'Account is required' },
          ],
        },
      },
      {
        id: 'inventoryTrackingMethod',
        type: 'select2',
        name: 'Tracking Method',
        props: {
          disabled: !inventoryEnabled,
          showSearch: true,
          optionFilterProp: 'label',
          options: [
            { label: 'None', value: 'None' },
            { label: 'Track Serial Number', value: 'Serial' },
            { label: 'Track Batches', value: 'Batch' },
          ],
        },
        options: {
          rules: [
            {
              required: inventoryEnabled,
              message: 'Tracking method is required',
            },
          ],
        },
      },
      {
        id: 'openingStock',
        type: 'number',
        name: 'Opening Stock',
        props: { disabled: !inventoryEnabled },
      },
      {
        id: 'openingStockRate',
        type: 'price',
        name: 'Opening Stock Rate per Unit',
        props: { disabled: !inventoryEnabled },
        options: {
          rules: [
            {
              required: inventoryEnabled && openingStockAvailable,
              message: 'Price is required',
            },
          ],
        },
      },
      {
        id: 'reorderPoint',
        type: 'number',
        name: 'Reorder Point',
        props: { disabled: !inventoryEnabled },
      },
    ];
    return formSchema;
  }, [business, inventoryEnabled, openingStockAvailable]);

  const submit = useCallback(
    (values: Partial<Item>) => {
      setSaving(true);
      const data: Partial<Item> = {
        ...values,
        sellable: salesEnabled,
        purchasable: purchasesEnabled,
        trackInventory: inventoryEnabled,
      };

      if (!salesEnabled) {
        delete data.sellingPrice;
        delete data.sellingAccount;
        delete data.sellingDescription;
      }
      if (!purchasesEnabled) {
        delete data.purchasingPrice;
        delete data.purchasingAccount;
        delete data.purchasingDescription;
      }
      if (!inventoryEnabled) {
        delete data.inventoryAccount;
        delete data.openingStock;
        delete data.openingStockRate;
        delete data.reorderPoint;
      }
      if (item) {
        connection
          .patch<Item>(`${business.businessId}/items/${item.itemId}`, data)
          .then((res) => {
            setVisible(false);
            events.emit('ITEM', { action: 'UPDATE', data: res.data });
            notification.success({
              message: 'Successfully updated.',
              description: 'Successfully updated the item',
            });
          })
          .catch((err) => {
            if (!setFormValidationErrors(form, err)) {
              Modal.error({
                title: 'Error',
                content: 'Something went wrong. Please try again later.',
              });
            }
          })
          .finally(() => setSaving(false));
      } else {
        connection
          .post<Item>(`${business.businessId}/items`, data)
          .then((res) => {
            setVisible(false);
            events.emit('ITEM', { action: 'CREATE', data: res.data });
            notification.success({
              message: 'Successfully created.',
              description: 'Successfully created the item',
            });
          })
          .catch((err) => {
            if (!setFormValidationErrors(form, err)) {
              Modal.error({
                title: 'Error',
                content: 'Something went wrong. Please try again later.',
              });
            }
          })
          .finally(() => setSaving(false));
      }
    },
    [
      business.businessId,
      connection,
      events,
      form,
      inventoryEnabled,
      item,
      purchasesEnabled,
      salesEnabled,
    ],
  );

  useEffect(() => {
    if (!purchasesEnabled || !salesEnabled) {
      setInventoryEnabled(false);
    }

    if (!salesEnabled) {
      form.resetFields([
        'sellingPrice',
        'sellingAccount',
        'sellingTax',
        'sellingDescription',
      ]);
    }

    if (!purchasesEnabled) {
      form.resetFields([
        'purchasingPrice',
        'purchasingAccount',
        'purchasingTax',
        'purchasingDescription',
      ]);
    }
  }, [form, purchasesEnabled, salesEnabled]);

  useEffect(() => {
    if (!inventoryEnabled) {
      form.resetFields([
        'inventoryAccount',
        'openingStock',
        'openingStockRate',
        'reorderPoint',
      ]);
    }
  }, [form, inventoryEnabled]);

  return (
    <Drawer
      title={title}
      open={visible}
      onClose={() => setVisible(false)}
      destroyOnClose={true}
      footer={
        <div className="flex justify-between gap-1">
          <div className="w-full flex justify-end gap-1">
            <Button onClick={() => setVisible(false)} disabled={loading}>
              Cancel
            </Button>
            <Button
              type="primary"
              loading={saving}
              onClick={form.submit}
              disabled={loading}
            >
              OK
            </Button>
          </div>
        </div>
      }
      width={996}
    >
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={submit}
          onValuesChange={(changedValues) => {
            if (Object.hasOwn(changedValues, 'openingStock')) {
              setOpeningStockAvailable(changedValues.openingStock > 0);
            }
          }}
        >
          <Form.Items schema={basicSchema} />
          <Divider />
          <Form.Items schema={otherInfoSchema} />
          <Divider />
          <div className="w-full grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <Checkbox
                checked={salesEnabled}
                onChange={(e) => setSalesEnabled(e.target.checked)}
              >
                <span className="text-lg font-medium">Sales Information</span>
              </Checkbox>
              <Form.Items schema={salesInfoSchema} />
            </div>
            <div className="flex flex-col gap-2">
              <Checkbox
                checked={purchasesEnabled}
                onChange={(e) => setPurchasesEnabled(e.target.checked)}
              >
                <span className="text-lg font-medium">
                  Purchase Information
                </span>
              </Checkbox>
              <Form.Items schema={purchaseInfoSchema} />
            </div>
          </div>
          <Divider />
          <Checkbox
            disabled={!salesEnabled || !purchasesEnabled}
            checked={inventoryEnabled}
            onChange={(e) => setInventoryEnabled(e.target.checked)}
          >
            <span className="text-lg font-medium">
              Track Inventory for this item
            </span>
          </Checkbox>
          <div className="pl-6 text-gray-500 text-sm">{`You cannot enable/disable inventory tracking once you've created transactions for this item`}</div>
          <div className="w-1/2">
            <Form.Items schema={inventoryInfoSchema} />
          </div>
        </Form>
      </Spin>
    </Drawer>
  );
};

export const useItemEditModal = () => useRef<ItemEditModalInstance>(null);

export default React.forwardRef<ItemEditModalInstance, Props>(ItemEditModal);
